import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, ArrowLeft, RefreshCw, MapPin, Users, Crown,
  DollarSign, CheckCircle, XCircle, Search, Plus, Edit, Trash2, Eye
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const FranchiseAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [franchises, setFranchises] = useState([]);
  const [packages, setPackages] = useState([]);
  const [stats, setStats] = useState({
    total: 0, city_captains: 0, district_captains: 0, total_earnings: 0
  });
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('franchises');
  
  // Modal states
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [showFranchiseModal, setShowFranchiseModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [editingFranchise, setEditingFranchise] = useState(null);
  
  const [packageForm, setPackageForm] = useState({
    name: '', type: 'city_captain', price: 25000, commission_rate: 5,
    benefits: '', requirements: '', is_active: true
  });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const query = filter !== 'all' ? `?franchise_type=${filter}` : '';
      const [franchisesRes, packagesRes] = await Promise.all([
        api.get(`/admin/franchises${query}`),
        api.get('/admin/franchise-packages')
      ]);
      const data = franchisesRes.data || [];
      setFranchises(data);
      setPackages(packagesRes.data || []);
      
      setStats({
        total: data.length,
        city_captains: data.filter(f => f.franchise_type === 'city_captain').length,
        district_captains: data.filter(f => f.franchise_type === 'district_captain').length,
        total_earnings: data.reduce((acc, f) => acc + (f.total_earnings || 0), 0)
      });
    } catch (error) {
      console.error('Failed to fetch franchises:', error);
      setFranchises([]);
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  // Package CRUD
  const savePackage = async () => {
    try {
      if (editingPackage) {
        await api.put(`/admin/franchise-package/${editingPackage.id}`, packageForm);
        toast.success('Package updated!');
      } else {
        await api.post('/admin/franchise-package', packageForm);
        toast.success('Package created!');
      }
      setShowPackageModal(false);
      setEditingPackage(null);
      resetPackageForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save package');
    }
  };

  const deletePackage = async (pkgId) => {
    if (!window.confirm('Delete this package?')) return;
    try {
      await api.delete(`/admin/franchise-package/${pkgId}`);
      toast.success('Package deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  const editPackage = (pkg) => {
    setEditingPackage(pkg);
    setPackageForm({
      name: pkg.name, type: pkg.type, price: pkg.price, commission_rate: pkg.commission_rate,
      benefits: pkg.benefits || '', requirements: pkg.requirements || '', is_active: pkg.is_active
    });
    setShowPackageModal(true);
  };

  const resetPackageForm = () => {
    setPackageForm({
      name: '', type: 'city_captain', price: 25000, commission_rate: 5,
      benefits: '', requirements: '', is_active: true
    });
  };

  // Franchise actions
  const suspendFranchise = async (franchiseId) => {
    if (!window.confirm('Suspend this franchise?')) return;
    try {
      await api.put(`/admin/franchise/${franchiseId}`, { status: 'suspended' });
      toast.success('Franchise suspended');
      fetchData();
    } catch (error) {
      toast.error('Failed to suspend franchise');
    }
  };

  const activateFranchise = async (franchiseId) => {
    try {
      await api.put(`/admin/franchise/${franchiseId}`, { status: 'active' });
      toast.success('Franchise activated');
      fetchData();
    } catch (error) {
      toast.error('Failed to activate franchise');
    }
  };

  const deleteFranchise = async (franchiseId) => {
    if (!window.confirm('Delete this franchise permanently? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/franchise/${franchiseId}`);
      toast.success('Franchise deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete franchise');
    }
  };

  const viewFranchise = (franchise) => {
    setEditingFranchise(franchise);
    setShowFranchiseModal(true);
  };

  const filteredFranchises = franchises.filter(f => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      f.city?.toLowerCase().includes(search) ||
      f.state?.toLowerCase().includes(search) ||
      f.district?.toLowerCase().includes(search) ||
      f.user?.name?.toLowerCase().includes(search) ||
      f.user?.email?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="franchise-admin">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/admin" className="text-muted-foreground hover:text-white text-sm flex items-center gap-1 mb-2">
              <ArrowLeft className="w-4 h-4" /> Back to Admin
            </Link>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Building2 className="w-8 h-8 text-amber-500" />
              Franchise Management
            </h1>
          </div>
          <button onClick={fetchData} className="p-3 rounded-xl bg-white/10 hover:bg-white/20">
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-card p-5">
            <Users className="w-7 h-7 text-blue-500 mb-2" />
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-sm text-muted-foreground">Total Franchises</p>
          </div>
          <div className="glass-card p-5">
            <MapPin className="w-7 h-7 text-green-500 mb-2" />
            <p className="text-2xl font-bold">{stats.city_captains}</p>
            <p className="text-sm text-muted-foreground">City Captains</p>
          </div>
          <div className="glass-card p-5">
            <Crown className="w-7 h-7 text-purple-500 mb-2" />
            <p className="text-2xl font-bold">{stats.district_captains}</p>
            <p className="text-sm text-muted-foreground">District Captains</p>
          </div>
          <div className="glass-card p-5">
            <DollarSign className="w-7 h-7 text-emerald-500 mb-2" />
            <p className="text-2xl font-bold">₹{stats.total_earnings.toLocaleString()}</p>
            <p className="text-sm text-muted-foreground">Total Earnings</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['franchises', 'packages'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium ${
                activeTab === tab ? 'bg-amber-500 text-white' : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Franchises Tab */}
        {activeTab === 'franchises' && (
          <>
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex gap-2">
                {['all', 'city_captain', 'district_captain'].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      filter === f ? 'bg-primary text-white' : 'bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    {f === 'all' ? 'All' : f.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </button>
                ))}
              </div>
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search by location or user..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/10 rounded-lg"
                />
              </div>
            </div>

            {/* Franchise List */}
            <div className="glass-card p-6">
              <h2 className="text-xl font-bold mb-4">All Franchises ({filteredFranchises.length})</h2>
              
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                </div>
              ) : filteredFranchises.length > 0 ? (
                <div className="space-y-4">
                  {filteredFranchises.map((franchise) => (
                    <div 
                      key={franchise.id} 
                      className={`p-4 rounded-xl border ${
                        franchise.franchise_type === 'district_captain' 
                          ? 'border-purple-500/30 bg-purple-500/5' 
                          : 'border-green-500/30 bg-green-500/5'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {franchise.franchise_type === 'district_captain' ? (
                            <Crown className="w-10 h-10 text-purple-500" />
                          ) : (
                            <MapPin className="w-10 h-10 text-green-500" />
                          )}
                          <div>
                            <h3 className="font-bold">
                              {franchise.franchise_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {franchise.city}, {franchise.state}
                              {franchise.district && ` (${franchise.district})`}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Owner: {franchise.user?.name || 'N/A'} ({franchise.user?.email || 'N/A'})
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Members Approved: {franchise.members_approved || 0} | 
                              Earnings: ₹{(franchise.total_earnings || 0).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            franchise.status === 'active' 
                              ? 'bg-green-500/20 text-green-500' 
                              : 'bg-red-500/20 text-red-500'
                          }`}>
                            {franchise.status}
                          </span>
                          <button
                            onClick={() => viewFranchise(franchise)}
                            className="p-2 rounded-lg bg-blue-500/20 text-blue-500 hover:bg-blue-500/30"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {franchise.status === 'active' ? (
                            <button
                              onClick={() => suspendFranchise(franchise.id)}
                              className="p-2 rounded-lg bg-orange-500/20 text-orange-500 hover:bg-orange-500/30"
                              title="Suspend"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => activateFranchise(franchise.id)}
                              className="p-2 rounded-lg bg-green-500/20 text-green-500 hover:bg-green-500/30"
                              title="Activate"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => deleteFranchise(franchise.id)}
                            className="p-2 rounded-lg bg-red-500/20 text-red-500 hover:bg-red-500/30"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Building2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No franchises found</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Franchises will appear here when users purchase City/District Captain packages
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Packages Tab */}
        {activeTab === 'packages' && (
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Franchise Packages</h2>
              <button
                onClick={() => { resetPackageForm(); setEditingPackage(null); setShowPackageModal(true); }}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg flex items-center gap-2 hover:bg-amber-600"
              >
                <Plus className="w-4 h-4" /> Add Package
              </button>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {packages.map((pkg) => (
                <div 
                  key={pkg.id} 
                  className={`p-6 rounded-xl border-2 ${
                    pkg.type === 'district_captain' 
                      ? 'border-purple-500/30 bg-purple-500/5' 
                      : 'border-green-500/30 bg-green-500/5'
                  } ${!pkg.is_active ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {pkg.type === 'district_captain' ? (
                        <Crown className="w-8 h-8 text-purple-500" />
                      ) : (
                        <MapPin className="w-8 h-8 text-green-500" />
                      )}
                      <div>
                        <h3 className="font-bold text-xl">{pkg.name}</h3>
                        <p className={`text-2xl font-bold ${pkg.type === 'district_captain' ? 'text-purple-500' : 'text-green-500'}`}>
                          ₹{pkg.price?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => editPackage(pkg)} 
                        className="p-2 bg-blue-500/20 text-blue-500 rounded hover:bg-blue-500/30"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => deletePackage(pkg.id)} 
                        className="p-2 bg-red-500/20 text-red-500 rounded hover:bg-red-500/30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-muted-foreground">Commission Rate:</span> {pkg.commission_rate}%</p>
                    <p><span className="text-muted-foreground">Benefits:</span> {pkg.benefits || 'N/A'}</p>
                    <p><span className="text-muted-foreground">Requirements:</span> {pkg.requirements || 'N/A'}</p>
                    <span className={`mt-2 inline-block text-xs px-2 py-1 rounded ${pkg.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                      {pkg.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Package Modal */}
      {showPackageModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-white/10 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{editingPackage ? 'Edit Package' : 'Create Package'}</h2>
            <div className="space-y-4">
              <input 
                type="text" 
                placeholder="Package Name" 
                value={packageForm.name}
                onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl" 
              />
              <select
                value={packageForm.type}
                onChange={(e) => setPackageForm({ ...packageForm, type: e.target.value })}
                className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
              >
                <option value="city_captain">City Captain</option>
                <option value="district_captain">District Captain</option>
              </select>
              <div className="grid grid-cols-2 gap-4">
                <input 
                  type="number" 
                  placeholder="Price (₹)" 
                  value={packageForm.price}
                  onChange={(e) => setPackageForm({ ...packageForm, price: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl" 
                />
                <input 
                  type="number" 
                  placeholder="Commission %" 
                  value={packageForm.commission_rate}
                  onChange={(e) => setPackageForm({ ...packageForm, commission_rate: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl" 
                />
              </div>
              <textarea
                placeholder="Benefits (one per line)"
                value={packageForm.benefits}
                onChange={(e) => setPackageForm({ ...packageForm, benefits: e.target.value })}
                className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                rows={3}
              />
              <textarea
                placeholder="Requirements (one per line)"
                value={packageForm.requirements}
                onChange={(e) => setPackageForm({ ...packageForm, requirements: e.target.value })}
                className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                rows={2}
              />
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={packageForm.is_active}
                  onChange={(e) => setPackageForm({ ...packageForm, is_active: e.target.checked })} 
                />
                Active
              </label>
              <div className="flex gap-3">
                <button 
                  onClick={() => { setShowPackageModal(false); setEditingPackage(null); }}
                  className="flex-1 py-3 bg-white/10 rounded-xl font-semibold"
                >
                  Cancel
                </button>
                <button 
                  onClick={savePackage} 
                  className="flex-1 py-3 bg-amber-500 text-white rounded-xl font-semibold"
                >
                  {editingPackage ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Franchise Detail Modal */}
      {showFranchiseModal && editingFranchise && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-white/10 rounded-2xl p-6 max-w-lg w-full">
            <h2 className="text-xl font-bold mb-4">Franchise Details</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="font-medium">{editingFranchise.franchise_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <p className={`font-medium ${editingFranchise.status === 'active' ? 'text-green-500' : 'text-red-500'}`}>
                    {editingFranchise.status}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Location</p>
                <p className="font-medium">
                  {editingFranchise.city}, {editingFranchise.state}
                  {editingFranchise.district && ` (${editingFranchise.district})`}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Owner</p>
                <p className="font-medium">{editingFranchise.user?.name || 'N/A'}</p>
                <p className="text-sm text-muted-foreground">{editingFranchise.user?.email}</p>
                <p className="text-sm text-muted-foreground">{editingFranchise.user?.whatsapp_number}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Members Approved</p>
                  <p className="font-medium text-xl">{editingFranchise.members_approved || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Earnings</p>
                  <p className="font-medium text-xl text-green-500">₹{(editingFranchise.total_earnings || 0).toLocaleString()}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Purchase Amount</p>
                <p className="font-medium">₹{(editingFranchise.purchase_amount || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Purchased At</p>
                <p className="font-medium">{editingFranchise.purchased_at ? new Date(editingFranchise.purchased_at).toLocaleString() : 'N/A'}</p>
              </div>
              <button 
                onClick={() => setShowFranchiseModal(false)}
                className="w-full py-3 bg-white/10 rounded-xl font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FranchiseAdmin;
